import { and, desc, eq, isNull, sql } from "@-/com.npmjs.drizzle-orm";
import type { ActorDatabase } from "@-/dev.improve.haniverse.database.actor";
import { actorSchema } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import {
  cardContentKindDrillKind,
  resolveUserCardContentKind,
  resolveUserCardSubject,
} from "@-/dev.improve.haniverse.lib.cards.card-content";
import { getUserCardStage } from "@-/dev.improve.haniverse.lib.cards.user-card.mastery";
import type { LearningStatsRecentStageEntriesData } from "@-/dev.improve.haniverse.lib.learning-stats.entities";
import { LEARNING_STATS_HISTORY_PAGE_SIZE } from "#read-streaming-review-history";
import {
  createRecentStageEntriesAccumulator,
  type RecentStageEntryCandidate,
} from "#recent-stage-entries-accumulator";
import { runLearningStatsQueryStep } from "#run-learning-stats-query-step";

type ReviewCursor = { reviewedAt: string; rowId: number };
const reviews = actorSchema.userCardReview;
const logs = actorSchema.fsrsCardReviewLog;

// The same active-card set the core slice counts, plus the overlay columns a tile shows.
const RECENT_STAGE_ENTRY_USER_CARDS_CONFIG = {
  columns: { deletedAt: true, id: true, manuallyMasteredAt: true, startedStudyingAt: true },
  where: { fsrsCardId: { isNotNull: true } },
  with: {
    fsrsCard: { columns: { lastReview: true, scheduledDays: true } },
    userGraphemeMeaningCard: { columns: { grapheme: true } },
    userGraphemePronunciationCard: { columns: { grapheme: true, pinyin: true } },
    userWordSentenceCard: { columns: { kind: true, word: true } },
  },
} as const;

/** Newest-first review page, walking the live `reviewed_at` index backwards with rowid ties. */
function recentStageEntryReviewPageQuery(actorDb: ActorDatabase, before: ReviewCursor | null) {
  const reviewRowId = sql<number>`${reviews}._rowid_`;
  return actorDb(import.meta.location)
    .select({
      cardId: reviews.cardId,
      reviewedAt: reviews.reviewedAt,
      rowId: reviewRowId,
      scheduledDays: logs.scheduledDays,
    })
    .from(reviews)
    .leftJoin(logs, and(eq(reviews.fsrsCardReviewLogId, logs.id), isNull(logs.deletedAt)))
    .where(
      and(
        isNull(reviews.deletedAt),
        before === null
          ? undefined
          : sql`(${reviews.reviewedAt}, ${reviewRowId}) < (${before.reviewedAt}, ${before.rowId})`,
      ),
    )
    .orderBy(desc(reviews.reviewedAt), desc(reviewRowId))
    .limit(LEARNING_STATS_HISTORY_PAGE_SIZE);
}

function assertNotCancelled(signal?: AbortSignal) {
  if (signal?.aborted) {
    throw new Error("Learning Stats recent stage entries read was cancelled");
  }
}

/**
 * Per stage, the cards that most recently entered it. Pages the review history newest first and
 * stops as soon as every stage's most recent entries are settled, so a long history is only read
 * back as far as the oldest listed entry.
 */
export async function readRecentStageEntries({
  actorDb,
  signal,
}: {
  actorDb: ActorDatabase;
  signal?: AbortSignal;
}): Promise<LearningStatsRecentStageEntriesData> {
  const cardRows = await runLearningStatsQueryStep("recent stage entry cards", () =>
    actorDb(import.meta.location).query.userCard.findMany(RECENT_STAGE_ENTRY_USER_CARDS_CONFIG),
  );
  const candidates: RecentStageEntryCandidate[] = [];
  for (const row of cardRows) {
    if (row.deletedAt !== null) {
      continue;
    }
    const contentKind = resolveUserCardContentKind(row);
    const subject = resolveUserCardSubject({
      userGraphemeMeaningCard: row.userGraphemeMeaningCard ?? null,
      userGraphemePronunciationCard: row.userGraphemePronunciationCard ?? null,
      userWordSentenceCard: row.userWordSentenceCard ?? null,
    });
    // A card whose overlay has not synced yet has nothing to show on a tile, and a quiz has no
    // glyph to tile (this read never joins quiz overlays).
    if (contentKind === null || contentKind === "quiz" || subject === null) {
      continue;
    }
    candidates.push({
      cardType: cardContentKindDrillKind(contentKind),
      lastReview: row.fsrsCard?.lastReview ?? null,
      manuallyMasteredAt: row.manuallyMasteredAt,
      pinyin: row.userGraphemePronunciationCard?.pinyin ?? null,
      stage: getUserCardStage({
        fsrsCard: row.fsrsCard ?? null,
        manuallyMasteredAt: row.manuallyMasteredAt,
      }),
      startedStudyingAt: row.startedStudyingAt,
      subject: subject.text,
      userCardId: row.id,
    });
  }

  const accumulator = createRecentStageEntriesAccumulator(candidates);
  let before: ReviewCursor | null = null;
  while (!accumulator.isDone()) {
    assertNotCancelled(signal);
    const page = await runLearningStatsQueryStep("recent stage entry review page", async () =>
      recentStageEntryReviewPageQuery(actorDb, before),
    );
    assertNotCancelled(signal);
    for (const row of page) {
      accumulator.addReview(row);
    }
    const last = page.at(-1);
    if (last === undefined || page.length < LEARNING_STATS_HISTORY_PAGE_SIZE) {
      return accumulator.finish({ exhausted: true });
    }
    before = { reviewedAt: last.reviewedAt, rowId: last.rowId };
  }
  return accumulator.finish({ exhausted: false });
}
